<script setup>
import { computed } from 'vue';
import { BarChart, DonutChart, LineChart } from '@chatwoot/viz';

const props = defineProps({
  visualization: {
    type: String,
    default: 'bar_chart',
  },
  // `{ categories, series }` for bar and line charts, `{ segments }` for pie ones.
  // See `helpers/reportChartHelpers` for the builders that produce them.
  data: {
    type: Object,
    required: true,
  },
  ariaLabel: {
    type: String,
    required: true,
  },
  // Sets the drawing aspect ratio against the chart's 960px viewBox; the SVG itself always
  // stretches to the container width, so this is a target height rather than a fixed one.
  height: {
    type: Number,
    default: 320,
  },
  // Thins out the x-axis labels, so only pass it when the categories are time periods.
  timeseries: {
    type: Boolean,
    default: false,
  },
  // Percentage value mode stacks each period's bars to 100%; see `buildChartProps`.
  stacked: {
    type: Boolean,
    default: false,
  },
  yDomain: {
    type: Array,
    default: undefined,
  },
  formatValue: {
    type: Function,
    default: undefined,
  },
  // Makes points, bars and slices clickable, emitting viz's click payload as `itemClick`.
  drillable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['itemClick']);

// viz only makes items clickable (cursor, focus, button role) when it gets a handler.
const onItemClick = computed(() =>
  props.drillable ? click => emit('itemClick', click) : undefined
);

const isPie = computed(() => props.visualization === 'pie_chart');

const isLine = computed(() => props.visualization === 'line_chart');

const cartesianChart = computed(() => (isLine.value ? LineChart : BarChart));

// Only forward what the target chart accepts, and only when set, so viz keeps its own
// defaults otherwise. `stacked` is a BarChart prop; LineChart has no equivalent.
// The donut is deliberately left out: its values stay absolute in percentage mode,
// because it labels every slice with its share already.
const cartesianProps = computed(() => ({
  ...(isLine.value ? {} : { stacked: props.stacked }),
  ...(props.yDomain ? { yDomain: props.yDomain } : {}),
  ...(props.formatValue ? { formatValue: props.formatValue } : {}),
}));

// viz draws a legend for donuts only, and a lone series does not need one: the colour starts
// carrying meaning once a chart plots more than one metric or dimension value.
const legendSeries = computed(() =>
  props.data.series?.length > 1 ? props.data.series : []
);
</script>

<template>
  <DonutChart
    v-if="isPie"
    :data="props.data"
    :aria-label="props.ariaLabel"
    :on-item-click="onItemClick"
    class="[--cw-viz-donut-label-color:rgb(var(--slate-11))] [--cw-viz-donut-legend-value-color:rgb(var(--slate-12))] [--cw-viz-donut-tooltip-border-color:rgb(var(--border-strong))] [--cw-viz-donut-tooltip-background:rgb(var(--solid-2))] [--cw-viz-donut-tooltip-label-color:rgb(var(--slate-11))] [--cw-viz-donut-tooltip-value-color:rgb(var(--slate-12))] [--cw-viz-donut-tooltip-description-color:rgb(var(--slate-10))]"
  />
  <div
    v-else
    class="flex flex-col min-w-0 [--cw-viz-bar-label-color:rgb(var(--slate-11))] [--cw-viz-bar-axis-color:rgb(var(--slate-6))] [--cw-viz-bar-zero-line-color:rgb(var(--slate-8))] [--cw-viz-bar-value-color:rgb(var(--slate-11))] [--cw-viz-bar-tooltip-border-color:rgb(var(--border-strong))] [--cw-viz-bar-tooltip-color:rgb(var(--slate-12))] [--cw-viz-bar-tooltip-background:rgb(var(--solid-2))] [--cw-viz-bar-tooltip-label-color:rgb(var(--slate-11))] [--cw-viz-bar-tooltip-active-color:rgb(var(--slate-12))] [--cw-viz-line-label-color:rgb(var(--slate-11))] [--cw-viz-line-axis-color:rgb(var(--slate-6))] [--cw-viz-line-tooltip-border-color:rgb(var(--border-strong))] [--cw-viz-line-tooltip-color:rgb(var(--slate-12))] [--cw-viz-line-tooltip-background:rgb(var(--solid-2))] [--cw-viz-line-tooltip-label-color:rgb(var(--slate-11))] [--cw-viz-line-tooltip-active-color:rgb(var(--slate-12))]"
  >
    <ul v-if="legendSeries.length" class="flex flex-wrap gap-x-4 gap-y-1 mb-3">
      <li
        v-for="series in legendSeries"
        :key="series.id"
        class="flex items-center gap-1.5 text-xs text-n-slate-11"
      >
        <span
          class="w-2.5 h-2.5 rounded-sm shrink-0"
          :style="{ backgroundColor: series.color }"
        />
        {{ series.label }}
      </li>
    </ul>
    <component
      :is="cartesianChart"
      :data="props.data"
      :aria-label="props.ariaLabel"
      :height="props.height"
      :timeseries="props.timeseries"
      :show-values="false"
      :on-item-click="onItemClick"
      v-bind="cartesianProps"
    />
  </div>
</template>
